import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HeartHandshake, Search, MapPin, Shield, Building2, Users, FileText,
  Calendar, MessageSquare, CreditCard, ChevronRight, Check, X, Bell,
  Share2, Copy, Mail, ExternalLink, Filter, Stethoscope, Heart, Brain,
  Activity, Moon, Footprints, AlertCircle, Clock, Lock, Unlock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { BottomNav } from '../components/BottomNav';
import { ThemeToggle } from '../components/ThemeToggle';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Data categories for consent
const dataCategories = [
  { id: 'biometrics', name: 'Biometrics', icon: Heart, description: 'HRV, heart rate, sleep quality, SpO2' },
  { id: 'behavior', name: 'Behavior & Adherence', icon: Activity, description: 'Workout and meal compliance' },
  { id: 'mood', name: 'Mood', icon: Brain, description: 'Daily mood check-ins and trends' },
  { id: 'activity', name: 'Activity', icon: Footprints, description: 'Steps, exercise, movement patterns' },
  { id: 'labs', name: 'Labs', icon: Stethoscope, description: 'Connected lab results (if available)' },
];

// Mock providers
const mockProviders = [
  { id: 1, name: 'Dr. Sarah Kim', specialty: 'Primary Care', location: 'Downtown Clinic', distance: '2.1 mi', insurance: ['Medicaid', 'Blue Cross'], isHealthLink: true },
  { id: 2, name: 'Dr. Michael Chen', specialty: 'Cardiology', location: 'Heart Health Center', distance: '4.5 mi', insurance: ['Medicaid', 'Aetna'], isHealthLink: true },
  { id: 3, name: 'Dr. Emily Rodriguez', specialty: 'Mental Health', location: 'Wellness Partners', distance: '3.2 mi', insurance: ['Medicaid', 'United'], isHealthLink: false },
  { id: 4, name: 'Dr. James Wilson', specialty: 'Sleep Medicine', location: 'Sleep & Rest Clinic', distance: '5.8 mi', insurance: ['Blue Cross', 'Cigna'], isHealthLink: true },
];

// Mock community resources
const communityResources = [
  { id: 1, name: 'Community Food Bank', category: 'Food Assistance', description: 'Free groceries and meal programs', phone: '(555) 123-4567' },
  { id: 2, name: 'Metro Transit Assistance', category: 'Transportation', description: 'Reduced fare programs for medical appointments', phone: '(555) 234-5678' },
  { id: 3, name: 'Mental Health Helpline', category: 'Mental Health', description: '24/7 support and counseling referrals', phone: '(555) 345-6789' },
  { id: 4, name: 'Community Health Center', category: 'Low-Cost Care', description: 'Sliding scale fee primary care', phone: '(555) 456-7890' },
  { id: 5, name: 'Housing Assistance Program', category: 'Housing', description: 'Emergency housing and rental assistance', phone: '(555) 567-8901' },
];

// Mock provider requests
const mockRequests = [
  { id: 1, provider: 'Dr. Sarah Kim', categories: ['mood', 'activity'], requestDate: '2026-01-14', expiresIn: 5 },
];

export default function ConnectToCarePage() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilters, setSelectedFilters] = useState({ insurance: '', specialty: '' });
  const [linkedProviders, setLinkedProviders] = useState([]);
  const [dataConsent, setDataConsent] = useState({
    biometrics: false,
    behavior: false,
    mood: false,
    activity: false,
    labs: false,
  });
  const [showShareModal, setShowShareModal] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [appointments, setAppointments] = useState([
    { id: 1, provider: 'Dr. Sarah Kim', date: '2026-01-20', time: '10:00 AM', type: 'Follow-up' },
  ]);
  const [insurance, setInsurance] = useState({ carrier: '', planId: '', memberId: '' });

  // Filter providers based on search and filters
  const filteredProviders = mockProviders.filter(provider => {
    const matchesSearch = provider.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         provider.specialty.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesInsurance = !selectedFilters.insurance || provider.insurance.includes(selectedFilters.insurance);
    return matchesSearch && matchesInsurance;
  });

  const handleLinkProvider = (provider) => {
    setSelectedProvider(provider);
    setShowConsentModal(true);
  };

  const confirmLinkProvider = () => {
    if (selectedProvider) {
      setLinkedProviders([...linkedProviders, selectedProvider]);
      toast.success(`Connected with ${selectedProvider.name}`);
      setShowConsentModal(false);
      setSelectedProvider(null);
    }
  };

  const handleToggleConsent = (categoryId) => {
    setDataConsent(prev => ({ ...prev, [categoryId]: !prev[categoryId] }));
  };

  const handleShareSummary = (method) => {
    toast.success(`Summary ${method === 'copy' ? 'copied to clipboard' : method === 'email' ? 'sent via email' : 'shared with provider'}`);
    setShowShareModal(false);
  };

  const handleApproveRequest = (requestId) => {
    toast.success('Data access approved');
  };

  const handleDenyRequest = (requestId) => {
    toast.info('Request denied');
  };

  return (
    <div className="min-h-screen bg-background pb-nav" data-testid="connect-to-care-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary/10">
              <HeartHandshake size={20} className="text-primary" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg">Connect to Care</h1>
              <p className="text-xs text-muted-foreground">Bridge insights to action</p>
            </div>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="px-4 py-6 space-y-6">
        {/* Care Status Banner */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Card className="glass-card p-4 border-l-4 border-l-primary" data-testid="care-status-banner">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-xl bg-primary/10">
                <Shield size={18} className="text-primary" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-sm">Your Care Network</h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {linkedProviders.length > 0 
                    ? `${linkedProviders.length} provider${linkedProviders.length > 1 ? 's' : ''} connected`
                    : 'No providers connected yet'}
                </p>
                {mockRequests.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-[#F8B64C]">
                    <Bell size={12} />
                    <span>{mockRequests.length} pending request{mockRequests.length > 1 ? 's' : ''}</span>
                  </div>
                )}
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                className="rounded-full text-xs"
                onClick={() => setShowShareModal(true)}
                data-testid="share-summary-btn"
              >
                <Share2 size={12} className="mr-1" />
                Share Summary
              </Button>
            </div>
          </Card>
        </motion.section>

        {/* Quick Navigation Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4">
          {[
            { id: 'overview', label: 'Overview', icon: HeartHandshake },
            { id: 'providers', label: 'Providers', icon: Stethoscope },
            { id: 'community', label: 'Community', icon: Users },
            { id: 'requests', label: 'Requests', icon: Bell },
            { id: 'insurance', label: 'Insurance', icon: CreditCard },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveSection(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
                activeSection === tab.id
                  ? 'bg-primary text-white'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={14} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Overview Section */}
        <AnimatePresence mode="wait">
          {activeSection === 'overview' && (
            <motion.div
              key="overview"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {/* Provider Requests */}
              {mockRequests.length > 0 && (
                <Card className="glass-card p-4 border-l-4 border-l-[#F8B64C]" data-testid="pending-requests-card">
                  <h3 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <Bell size={14} className="text-[#F8B64C]" />
                    Requests From Providers
                  </h3>
                  {mockRequests.map((request) => (
                    <div key={request.id} className="bg-muted/30 rounded-xl p-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-medium">{request.provider}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            Requested access to: {request.categories.map(c => 
                              dataCategories.find(dc => dc.id === c)?.name
                            ).join(', ')}
                          </p>
                          <p className="text-xs text-[#F8B64C] mt-1 flex items-center gap-1">
                            <Clock size={10} />
                            Expires in {request.expiresIn} days
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2 mt-3">
                        <Button 
                          size="sm" 
                          className="flex-1 rounded-full text-xs"
                          onClick={() => handleApproveRequest(request.id)}
                          data-testid={`approve-request-${request.id}`}
                        >
                          <Check size={12} className="mr-1" />
                          Approve
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 rounded-full text-xs"
                          onClick={() => handleDenyRequest(request.id)}
                          data-testid={`deny-request-${request.id}`}
                        >
                          <X size={12} className="mr-1" />
                          Deny
                        </Button>
                      </div>
                    </div>
                  ))}
                </Card>
              )}

              {/* Linked Providers */}
              <Card className="glass-card p-4" data-testid="linked-providers-card">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-sm flex items-center gap-2">
                    <Stethoscope size={14} className="text-primary" />
                    Your Care Team
                  </h3>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-xs"
                    onClick={() => setActiveSection('providers')}
                  >
                    Find Provider
                    <ChevronRight size={12} className="ml-1" />
                  </Button>
                </div>
                {linkedProviders.length > 0 ? (
                  <div className="space-y-2">
                    {linkedProviders.map((provider) => (
                      <div key={provider.id} className="flex items-center gap-3 p-2 bg-muted/30 rounded-xl">
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <Stethoscope size={18} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{provider.name}</p>
                          <p className="text-xs text-muted-foreground">{provider.specialty}</p>
                        </div>
                        <Badge variant="outline" className="text-xs">
                          <Lock size={10} className="mr-1" />
                          Data Shared
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-muted-foreground">
                    <Users size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No providers connected yet</p>
                    <p className="text-xs">Find and connect with care providers</p>
                  </div>
                )}
              </Card>

              {/* Upcoming Appointments */}
              <Card className="glass-card p-4" data-testid="appointments-card">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-3">
                  <Calendar size={14} className="text-primary" />
                  Upcoming Appointments
                </h3>
                {appointments.length > 0 ? (
                  <div className="space-y-2">
                    {appointments.map((apt) => (
                      <div key={apt.id} className="flex items-center gap-3 p-3 bg-muted/30 rounded-xl">
                        <div className="w-12 h-12 rounded-xl bg-primary/10 flex flex-col items-center justify-center">
                          <span className="text-xs font-bold text-primary">
                            {new Date(apt.date).toLocaleDateString('en-US', { day: 'numeric' })}
                          </span>
                          <span className="text-[9px] text-muted-foreground uppercase">
                            {new Date(apt.date).toLocaleDateString('en-US', { month: 'short' })}
                          </span>
                        </div>
                        <div className="flex-1">
                          <p className="text-sm font-medium">{apt.provider}</p>
                          <p className="text-xs text-muted-foreground">{apt.type} • {apt.time}</p>
                        </div>
                        <ChevronRight size={16} className="text-muted-foreground" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">No upcoming appointments</p>
                )}
                <Button variant="outline" className="w-full mt-3 rounded-full text-xs" disabled>
                  <Calendar size={12} className="mr-1" />
                  Schedule Appointment (Coming Soon)
                </Button>
              </Card>

              {/* Quick Links */}
              <div className="grid grid-cols-2 gap-3">
                <Card 
                  className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setActiveSection('community')}
                >
                  <Users size={20} className="text-primary mb-2" />
                  <p className="text-sm font-medium">Community Resources</p>
                  <p className="text-xs text-muted-foreground">Food, transport, support</p>
                </Card>
                <Card 
                  className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                  onClick={() => setShowShareModal(true)}
                >
                  <FileText size={20} className="text-primary mb-2" />
                  <p className="text-sm font-medium">HealthLink Summary</p>
                  <p className="text-xs text-muted-foreground">Share with providers</p>
                </Card>
              </div>
            </motion.div>
          )}

          {/* Providers Section */}
          {activeSection === 'providers' && (
            <motion.div
              key="providers"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              {/* Search */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search providers by name or specialty..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 rounded-full"
                  data-testid="provider-search-input"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-2 overflow-x-auto pb-2">
                <select
                  value={selectedFilters.insurance}
                  onChange={(e) => setSelectedFilters(f => ({ ...f, insurance: e.target.value }))}
                  className="px-3 py-1.5 rounded-full bg-muted text-xs border-0 focus:ring-2 focus:ring-primary"
                >
                  <option value="">All Insurance</option>
                  <option value="Medicaid">Medicaid</option>
                  <option value="Blue Cross">Blue Cross</option>
                  <option value="Aetna">Aetna</option>
                  <option value="United">United</option>
                </select>
                <Button variant="outline" size="sm" className="rounded-full text-xs">
                  <Filter size={12} className="mr-1" />
                  More Filters
                </Button>
              </div>

              {/* Provider List */}
              <div className="space-y-3">
                {filteredProviders.map((provider) => {
                  const isLinked = linkedProviders.some(p => p.id === provider.id);
                  return (
                    <Card 
                      key={provider.id} 
                      className={`glass-card p-4 ${provider.isHealthLink ? 'border-l-4 border-l-primary' : ''}`}
                      data-testid={`provider-card-${provider.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <Stethoscope size={20} className="text-primary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-sm">{provider.name}</p>
                            {provider.isHealthLink && (
                              <Badge className="text-[9px] bg-primary/10 text-primary border-0">
                                HealthLink360
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">{provider.specialty}</p>
                          <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                            <MapPin size={10} />
                            {provider.location} • {provider.distance}
                          </div>
                          <div className="flex flex-wrap gap-1 mt-2">
                            {provider.insurance.map((ins) => (
                              <Badge key={ins} variant="secondary" className="text-[9px]">
                                {ins}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        {isLinked ? (
                          <Badge variant="outline" className="text-xs shrink-0">
                            <Check size={10} className="mr-1" />
                            Connected
                          </Badge>
                        ) : (
                          <Button 
                            size="sm" 
                            className="rounded-full text-xs shrink-0"
                            onClick={() => handleLinkProvider(provider)}
                            data-testid={`link-provider-${provider.id}`}
                          >
                            Connect
                          </Button>
                        )}
                      </div>
                    </Card>
                  );
                })}
              </div>
            </motion.div>
          )}

          {/* Community Resources Section */}
          {activeSection === 'community' && (
            <motion.div
              key="community"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              <Card className="glass-card p-4 bg-primary/5" data-testid="community-intro">
                <p className="text-sm">
                  Community resources based on your location and wellness goals. 
                  These services can help with non-clinical needs.
                </p>
              </Card>

              <div className="space-y-3">
                {communityResources.map((resource) => (
                  <Card key={resource.id} className="glass-card p-4" data-testid={`resource-${resource.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-xl bg-[#8BB89F]/10 shrink-0">
                        <Users size={18} className="text-[#8BB89F]" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-sm">{resource.name}</p>
                        <Badge variant="secondary" className="text-[9px] mt-1">
                          {resource.category}
                        </Badge>
                        <p className="text-xs text-muted-foreground mt-2">{resource.description}</p>
                        <p className="text-xs text-primary mt-2">{resource.phone}</p>
                      </div>
                      <Button variant="ghost" size="sm" className="shrink-0">
                        <ExternalLink size={14} />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            </motion.div>
          )}

          {/* Requests Section */}
          {activeSection === 'requests' && (
            <motion.div
              key="requests"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              <Card className="glass-card p-4" data-testid="consent-settings">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-4">
                  <Lock size={14} className="text-primary" />
                  Data Sharing Preferences
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Control what data categories can be shared with your care providers.
                </p>
                <div className="space-y-3">
                  {dataCategories.map((category) => (
                    <div key={category.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <category.icon size={14} className="text-primary" />
                        </div>
                        <div>
                          <p className="text-sm font-medium">{category.name}</p>
                          <p className="text-xs text-muted-foreground">{category.description}</p>
                        </div>
                      </div>
                      <Switch
                        checked={dataConsent[category.id]}
                        onCheckedChange={() => handleToggleConsent(category.id)}
                        data-testid={`consent-toggle-${category.id}`}
                      />
                    </div>
                  ))}
                </div>
              </Card>

              {mockRequests.length > 0 && (
                <Card className="glass-card p-4">
                  <h3 className="font-medium text-sm flex items-center gap-2 mb-3">
                    <Bell size={14} className="text-[#F8B64C]" />
                    Pending Requests
                  </h3>
                  {mockRequests.map((request) => (
                    <div key={request.id} className="bg-muted/30 rounded-xl p-3 mb-2">
                      <p className="text-sm font-medium">{request.provider}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Wants access to: {request.categories.join(', ')}
                      </p>
                      <div className="flex gap-2 mt-3">
                        <Button size="sm" className="flex-1 rounded-full text-xs" onClick={() => handleApproveRequest(request.id)}>
                          Approve
                        </Button>
                        <Button size="sm" variant="outline" className="flex-1 rounded-full text-xs" onClick={() => handleDenyRequest(request.id)}>
                          Deny
                        </Button>
                      </div>
                    </div>
                  ))}
                </Card>
              )}
            </motion.div>
          )}

          {/* Insurance Section */}
          {activeSection === 'insurance' && (
            <motion.div
              key="insurance"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="space-y-4"
            >
              <Card className="glass-card p-4" data-testid="insurance-form">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-4">
                  <CreditCard size={14} className="text-primary" />
                  Insurance Information
                </h3>
                <p className="text-xs text-muted-foreground mb-4">
                  Store your insurance details for easy access. This information is kept secure and private.
                </p>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Insurance Carrier</label>
                    <Input
                      placeholder="e.g., Blue Cross Blue Shield"
                      value={insurance.carrier}
                      onChange={(e) => setInsurance(i => ({ ...i, carrier: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Plan ID</label>
                    <Input
                      placeholder="Your plan ID"
                      value={insurance.planId}
                      onChange={(e) => setInsurance(i => ({ ...i, planId: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-medium text-muted-foreground">Member ID</label>
                    <Input
                      placeholder="Your member ID"
                      value={insurance.memberId}
                      onChange={(e) => setInsurance(i => ({ ...i, memberId: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                  <Button className="w-full rounded-full" onClick={() => toast.success('Insurance information saved')}>
                    Save Insurance Info
                  </Button>
                </div>
              </Card>

              <Card className="glass-card p-4 bg-[#8BB89F]/5 border-l-4 border-l-[#8BB89F]">
                <h3 className="font-medium text-sm flex items-center gap-2 mb-2">
                  <Shield size={14} className="text-[#8BB89F]" />
                  Need Coverage?
                </h3>
                <p className="text-xs text-muted-foreground mb-3">
                  If you need help getting insurance coverage, we can connect you with resources.
                </p>
                <Button variant="outline" className="rounded-full text-xs">
                  <ExternalLink size={12} className="mr-1" />
                  Get Coverage Help
                </Button>
              </Card>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Share Summary Modal */}
      <AnimatePresence>
        {showShareModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4"
            onClick={() => setShowShareModal(false)}
          >
            <motion.div
              initial={{ y: 100 }}
              animate={{ y: 0 }}
              exit={{ y: 100 }}
              className="bg-card rounded-t-3xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="share-modal"
            >
              <h3 className="font-heading font-bold text-lg mb-2">Your HealthLink Summary</h3>
              <p className="text-sm text-muted-foreground mb-4">
                A structured report for your care providers.
              </p>
              
              <Card className="glass-card p-4 mb-4 text-xs space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Goal:</span>
                  <span className="font-medium">Improve Energy</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Focus Mode:</span>
                  <span className="font-medium">Stability</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">DTS Trend:</span>
                  <span className="font-medium text-[#8BB89F]">↑ Improving</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Top Driver:</span>
                  <span className="font-medium">Low sleep consistency</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">7-Day Adherence:</span>
                  <span className="font-medium">78%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Confidence:</span>
                  <span className="font-medium">Medium</span>
                </div>
              </Card>

              <div className="grid grid-cols-3 gap-2">
                <Button variant="outline" className="rounded-xl" onClick={() => handleShareSummary('copy')}>
                  <Copy size={16} />
                </Button>
                <Button variant="outline" className="rounded-xl" onClick={() => handleShareSummary('email')}>
                  <Mail size={16} />
                </Button>
                <Button className="rounded-xl" onClick={() => handleShareSummary('provider')}>
                  <Share2 size={16} />
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-2 mt-1 text-[10px] text-muted-foreground text-center">
                <span>Copy</span>
                <span>Email</span>
                <span>Share</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Consent Modal */}
      <AnimatePresence>
        {showConsentModal && selectedProvider && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowConsentModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-card rounded-3xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
              data-testid="consent-modal"
            >
              <h3 className="font-heading font-bold text-lg mb-2">Connect with {selectedProvider.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Choose what data to share with this provider.
              </p>
              
              <div className="space-y-2 mb-4">
                {dataCategories.map((category) => (
                  <div key={category.id} className="flex items-center justify-between p-3 bg-muted/30 rounded-xl">
                    <div className="flex items-center gap-2">
                      <category.icon size={14} className="text-primary" />
                      <span className="text-sm">{category.name}</span>
                    </div>
                    <Switch
                      checked={dataConsent[category.id]}
                      onCheckedChange={() => handleToggleConsent(category.id)}
                    />
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1 rounded-full"
                  onClick={() => setShowConsentModal(false)}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1 rounded-full"
                  onClick={confirmLinkProvider}
                  data-testid="confirm-connect-btn"
                >
                  <Check size={14} className="mr-1" />
                  Connect
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <BottomNav />
    </div>
  );
}
